import { Checkbox, Tooltip } from 'antd';
import Spinner from 'common/spinner/components/spinner';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Icon } from 'ui';
import PagingFooter from './paging-footer';
import { Header, HeaderColumn, HeaderLabel } from './table-helper';
import useDraggableRows from './useDraggableRows';

const Column = styled.td`
  flex: ${p => p.width ? `0 0 ${p.width}` : '1'};
  height: auto;
  min-height: ${p => p.labelInColumn ? 50 : 30}px;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  margin: auto 20px auto 0;
  overflow: hidden;
  ${p => p.colDragStyles};
`;

const Footer = styled.div`
  background-color: rgb(255, 255, 255);
`;

const Row = styled.tr`
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  user-select: auto;
  cursor: ${p => p.clickable ? 'pointer' : 'default'};
  border: ${p => p.isSelected ? '2px solid #0098cc !important' : 'none'};
  ${p => p.dragRowCss};
  ${p => p.extraStyles};
  ${p => p.fullWidth && 'padding-left: 20px'};
  ${p => !p.labelInColumn && p.showBottomBorder ? `
    border-bottom: 1px solid #d6d9db;
  ` : `
    ${!p.hasDetailRows && 'margin-bottom: 6px;'}
    ${p.labelInColumn && p.showBottomBorder &&
  'border-bottom: 1px solid rgba(217, 224, 230, 0.6);'}
  `}
`;

const DetailsRow = styled(Row)`
  margin-bottom: 12px;
  border-bottom: 0px;
  display: block;
  padding: 0 0 0 5%;
`;

const NoDataRow = styled(Row)`
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  padding: 10px 20px;
  justify-content: center;
  ${p => !p.labelInColumn && `
    border-bottom: none;
  `}
`;

const Label = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: rgba(100,116,124,0.85);
  height: auto;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const Value = styled.div`
  font-size: 16px;
  font-weight: 400;
  height: auto;
  min-height: 35px;
  display: flex;
  flex-flow: row wrap;
  align-items: center;
`;

const ValueText = styled.div`
  font-size: 16px;
  font-weight: 400;
  width: 100%;
  ${p => p.showFullValue ?
    `
      word-break: break-word;
    ` : `
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  ` }
`;

const Wrapper = styled.div`
  ${p => p.disabled &&
    `pointer-events: none;
    opacity: 0.4;`
  }
  width: ${p => p.width};
  height: auto;
`;

const Content = styled.tbody`
  height: ${p => p.height};
  display: grid;
  overflow: auto;
  ${p => p.maxHeight ? `max-height: ${p.maxHeight};` : ''}
`;

const TableHeader = styled.thead`
  display: grid;
`;

const OverflowWrapper = styled.table`
  position: relative;
  display: grid;
  white-space: nowrap;
  overflow: auto;
  width: 100%;
`;

const DragIcon = styled.div`
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  visibility: ${p => p.isDisabled ? 'hidden' : 'visible'};
  cursor: ${p => p.isDisabled ? 'not-allowed' : 'grab'};
  opacity: ${p => p.isDisabled ? '0.5' : '1'};
  ${p => p.iconDragStyle};
  i {
    color: rgba(0, 0, 0, 0.88);
    cursor: inherit !important;
  }
`;

const FooterWrapper = styled.div`
  height: 50px;
  width: 100%;
  display: flex;
  align-items: center;
  padding-left: 20px;
  border-top: 1px solid rgb(182, 187, 191);
`;

const Table = ({
  columns = [],
  data = [],
  intl,
  labelInColumn = false,
  width = '100%',
  fullWidth = false,
  height = '100%',
  isLoadingData = false,
  infiniteScroll = false,
  loadMore,
  totalDataCount,
  showFullValue = false,
  lightHeader = false,
  movableRows = false,
  onDrop,
  renderCustomHeader,
  renderCustomFooter,
  expandable = {},
  disabled = false,
  extraRowStyles = {},
  onRowClick,
  onRowSelect,
  selectedRowIds = [],
  activeRow = {},
  usePaging = false,
  pageSize = 10,
  maxHeight,
}) => {
  const [page, setPage] = useState(1);
  const [
    tableData,
    rowActions,
    dropzoneActions,
    rowStyles,
    colStyles,
    iconStyles,
  ] = useDraggableRows(data, onDrop);
  const hasCustomHeader = !!renderCustomHeader;
  const canSelectRow = !!onRowSelect;
  const tableCols = [
    movableRows && {
      id: 'drag',
      displayName: '',
      width: '30px',
      renderFn: row => <DragIcon
        id='drag-icon'
        isDisabled={row?.isHidden || disabled || row?.disableMove}
        iconDragStyle={iconStyles(row.id)}
      >
        <Icon className='fa-solid fa-grid' />
      </DragIcon>,
    },
    canSelectRow && {
      id: 'select',
      displayName: '',
      width: '30px',
      renderFn: row => <Checkbox
        checked={selectedRowIds.find(r => r === row.id)}
        disabled={!row?.id}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onChange={() => {
          if (onRowSelect && row?.id) {
            onRowSelect(row.id);
          }
        }}
      />,
    },
    ...columns,
  ].filter(r => r && !r.isHidden);

  const renderValue = (row, col) => {
    if (col.renderFn) {
      return col.tooltip && col.tooltip(row) ? <Tooltip
        title={col.tooltip(row)}
        mouseEnterDelay={1}
        placement='topLeft'
      >
        <ValueText showFullValue={showFullValue || col.showFullValue}>
          {col.renderFn(row)}
        </ValueText>
      </Tooltip> : <ValueText showFullValue={showFullValue || col.showFullValue}>
        {col.renderFn(row)}
      </ValueText>;
    }
    // eslint-disable-next-line no-nested-ternary
    return col.fieldName ?
      col.tooltip && col.tooltip(row) ? <Tooltip
        title={col.tooltip(row)}
        mouseEnterDelay={1}
        placement='topLeft'
      >
        <ValueText showFullValue={showFullValue || col.showFullValue}>
          {row[col.fieldName]}
        </ValueText>
      </Tooltip> : <ValueText showFullValue={showFullValue || col.showFullValue}>
        {row[col.fieldName]}
      </ValueText> :
      null;
  };

  const renderRows = () => {
    const dataList = usePaging ? tableData.slice(0, page * pageSize) : tableData;
    return dataList.length ? dataList?.map((d, i) => {
      const hasDetailRow = !!expandable?.expandedRowKeys?.filter(index => index === d?.id).length;
      return <>
        <Row
          data-test-id='table.row'
          extraStyles={{
            ...extraRowStyles,
            backgroundColor: activeRow?.id && activeRow?.id === d[activeRow.rowField] ?
              'rgba(232, 146, 124, 0.25) !important' :
              'transparent',
          }}
          key={d?.index || i}
          labelInColumn={labelInColumn}
          fullWidth={fullWidth}
          draggable={movableRows && !d?.isHidden && !disabled}
          dragRowCss={rowStyles(d?.id)}
          hasDetailRows={hasDetailRow}
          showBottomBorder={hasDetailRow || dataList?.length - 1 !== i}
          clickable={!!onRowClick && d?.id}
          isSelected={canSelectRow && selectedRowIds?.find(r => r === d?.id)}
          onClick={() => onRowClick && d?.id && onRowClick(d?.id)}
          {...rowActions(d?.index)}
        >
          {tableCols?.map(c =>
            <Column
              width={c?.width}
              key={c?.id}
              labelInColumn={labelInColumn}
              id={c?.id}
              colDragStyles={colStyles()}
            >
              {labelInColumn && <Label>{c?.displayName}</Label>}
              <Value>{renderValue(d, c)}</Value>
            </Column >,
          )}
        </Row >
        {hasDetailRow &&
          <DetailsRow>
            {expandable?.expandedRowRender(d)}
          </DetailsRow>
        }
      </>;
    },
    ) : <NoDataRow>
      {intl.formatMessage({ id: 'common.placeholders.NO_DATA_FOUND' })}
    </NoDataRow>;
  };

  const renderContent = () => {
    if (isLoadingData) {
      return <Spinner />;
    }
    if (infiniteScroll && !usePaging) {
      return <InfiniteScroll
        element='div'
        loadMore={loadMore}
        hasMore={tableData?.length < totalDataCount || false}
        loader={<Spinner key={0} />}
        useWindow={false}
        initialLoad={false}
        pageStart={1}
      >
        {renderRows()}
      </InfiniteScroll>;
    }
    return renderRows();
  };

  const renderPagingFooter = () => tableData?.length > pageSize &&
    tableData?.length - (page * pageSize) > 0 ?
    // eslint-disable-next-line
    <FooterWrapper>
      <PagingFooter
        pageSize={pageSize}
        remaining={tableData?.length - (page * pageSize)}
        showMore={() => setPage(p => p + 1)}
      />
    </FooterWrapper> :
    null;

  return (
    <Wrapper width={width} disabled={disabled} data-test-id='table-wrapper'>
      {renderCustomHeader && renderCustomHeader()}
      <OverflowWrapper>
        {!labelInColumn && <TableHeader>
          <Header fullWidth={fullWidth} light={lightHeader}>
            {tableCols?.map(c =>
              <HeaderColumn width={c.width} key={c.id}>
                <HeaderLabel light={lightHeader}>{c.displayName}</HeaderLabel>
              </HeaderColumn>,
            )}
          </Header>
        </TableHeader>}
        <Content
          headerMargin={hasCustomHeader}
          height={height}
          maxHeight={maxHeight}
          {...dropzoneActions}
        >
          {renderContent()}
        </Content>
      </OverflowWrapper>
      {usePaging && <Footer>{renderPagingFooter()}</Footer>}
      {renderCustomFooter && <Footer>{renderCustomFooter()}</Footer>}
    </Wrapper>
  );
};

Table.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  labelInColumn: PropTypes.bool,
  width: PropTypes.string,
  fullWidth: PropTypes.bool,
  height: PropTypes.string,
  isLoadingData: PropTypes.bool,
  infiniteScroll: PropTypes.bool,
  loadMore: PropTypes.func,
  totalDataCount: PropTypes.number,
  intl: PropTypes.object,
  lightHeader: PropTypes.bool,
  movableRows: PropTypes.bool,
  onDrop: PropTypes.func,
  showFullValue: PropTypes.bool,
  renderCustomFooter: PropTypes.func,
  renderCustomHeader: PropTypes.func,
  expandable: PropTypes.object,
  disabled: PropTypes.bool,
  extraStyles: PropTypes.object,
  extraRowStyles: PropTypes.object,
  onRowClick: PropTypes.func,
  onRowSelect: PropTypes.func,
  selectedRowIds: PropTypes.array,
  activeRow: PropTypes.object,
  usePaging: PropTypes.bool,
  pageSize: PropTypes.number,
  maxHeight: PropTypes.string,
};

export default injectIntl(Table);
